import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import React, { useEffect, useRef } from 'react';
import { MapPoint, ProposedCandidate, ProposedStop } from '../types';
import { ctrlWheelZoom } from './mapZoom';

interface Props {
  start: MapPoint;
  end: MapPoint;
  /** Mesta kroz koja pravac prolazi — linija na mapi. */
  corridor: MapPoint[];
  /** Stanice na turi, redom obilaska. */
  stops: ProposedStop[];
  /** Predlozi koji nisu uzeti. Klik ih dodaje. */
  candidates: ProposedCandidate[];
  /**
   * Preuzimanja tokova: mesta gde tura UZIMA robu internih prenosa. Nisu
   * stanice predloga — izvodi ih sastav ture — pa bi bez ovoga bila nevidljiva
   * na mapi, a vozilo tamo stvarno staje.
   */
  pickups?: { label: string; latitude: number; longitude: number; pieces: number }[];
  /**
   * Izbacuje stanicu sa ture. Ide na Ctrl+klik, ne na klik: crvena čioda se
   * otvara da bi se PROČITALA — koliko komada, koliko čeka, treba li dva čoveka
   * — pa bi obican klik izbacivao isporuku svaki put kad operater nešto
   * proverava. Vraća se jednim klikom, jer stanica ponovo postane plava.
   */
  onRemove: (stop: ProposedStop) => void;
  /** Linija stvarnog puta. Prazno kad servis za rutiranje nije podešen. */
  roadGeometry: [number, number][];
  onAdd: (candidate: ProposedCandidate) => void;
  /**
   * Menja se kad se kontejner promeni. Leaflet ne primeti promenu veličine sam,
   * pa bi posle prelaska u pun ekran ostao nacrtan po starim merama.
   */
  sizeToken?: string | number;
}

/** Zelena: polazište i povratak. Crvena: stanica na turi. Plava: predlog. */
function pin(kind: 'start' | 'stop' | 'candidate' | 'pickup', text: string): L.DivIcon {
  return L.divIcon({
    className: `rpin rpin--${kind}`,
    html: `<span>${text}</span>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13]
  });
}

/**
 * Mapa pravca.
 *
 * Postoji da bi operater video oblik ture, a ne samo spisak. Plava čioda ne
 * kaže „blizu si" nego koliko kilometara dodaje — jer je to jedini broj po kom
 * se stvarno odlučuje. Klik na plavu je stavlja na turu; ništa se ne dodaje
 * samo od sebe.
 */
export default function RouteMap({
  start, end, corridor, stops, candidates, pickups = [], roadGeometry, onAdd, onRemove, sizeToken
}: Props) {
  const container = useRef<HTMLDivElement | null>(null);
  const map = useRef<L.Map | null>(null);
  const layer = useRef<L.LayerGroup | null>(null);
  const add = useRef(onAdd);
  const remove = useRef(onRemove);

  useEffect(() => {
    add.current = onAdd;
    remove.current = onRemove;
  }, [onAdd, onRemove]);

  useEffect(() => {
    if (!container.current || map.current) return;

    const instance = L.map(container.current, { scrollWheelZoom: false })
      .setView([start.latitude, start.longitude], 9);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap'
    }).addTo(instance);

    const odjavi = ctrlWheelZoom(instance, container.current);

    map.current = instance;
    layer.current = L.layerGroup().addTo(instance);

    return () => {
      odjavi();
      instance.remove();
      map.current = null;
      layer.current = null;
    };
    // Mapa se pravi jednom. Polazište se ovde koristi samo za prvi pogled, a
    // pravi okvir se postavlja u prolazu ispod, pa nije u zavisnostima.
  }, [start.latitude, start.longitude]);

  useEffect(() => {
    const instance = map.current;
    const group = layer.current;
    if (!instance || !group) return;

    group.clearLayers();

    if (corridor.length > 1) {
      L.polyline(corridor.map((p) => [p.latitude, p.longitude] as [number, number]), {
        color: '#17527d',
        weight: 2,
        opacity: 0.45,
        dashArray: '6 6'
      })
        .bindTooltip(corridor.map((p) => p.label).join(' → '))
        .addTo(group);
    }

    if (roadGeometry.length > 1) {
      // Stvarni put. Cela tura je jedna linija, pa se povratak ne razlikuje —
      // kad je poznat put, razlika odlazak/povratak i nije bitna: vidi se kuda
      // se stvarno vozi, a ne kako se tačke spajaju.
      L.polyline(roadGeometry, { color: '#a12727', weight: 4, opacity: 0.75 })
        .bindTooltip('Put kroz sve stanice i nazad')
        .addTo(group);
    } else if (stops.length > 0) {
      // Bez rutiranja se crtaju prave linije između tačaka. Odlazak punom,
      // povratak tanko i isprekidano — povratak često preseca ceo prikaz i
      // zbunjuje, ali se ne briše: vozilo se stvarno vraća i to košta
      // četrdesetak kilometara, pa bi tura izgledala kraća nego što jeste.
      const outbound: [number, number][] = [
        [start.latitude, start.longitude],
        ...stops.map((s) => [s.latitude, s.longitude] as [number, number])
      ];

      L.polyline(outbound, { color: '#a12727', weight: 3, opacity: 0.75 }).addTo(group);

      const last = stops[stops.length - 1];

      L.polyline(
        [
          [last.latitude, last.longitude],
          [end.latitude, end.longitude]
        ],
        { color: '#a12727', weight: 1.5, opacity: 0.4, dashArray: '4 7' }
      )
        .bindTooltip('Povratak')
        .addTo(group);
    }

    L.marker([start.latitude, start.longitude], { icon: pin('start', '⌂'), zIndexOffset: 500 })
      .bindTooltip(`Polazište i povratak — ${start.label}`)
      .addTo(group);

    stops.forEach((stop, index) => {
      const marker = L.marker(
        [stop.latitude, stop.longitude],
        { icon: pin('stop', String(index + 1)), zIndexOffset: 300 }
      )
        .bindTooltip(
          `${index + 1}. ${stop.label}${stop.city ? `, ${stop.city}` : ''}`
          + `<br>${stop.pieces} kom · čeka ${stop.waitingDays} d`
          + (stop.originName ? `<br>prodao ${stop.originName}` : '')
          // Zašto stanica stoji tu gde stoji. Bez ovoga redosled izgleda
          // kao greška: mesto je blizu polazišta, a isporuka je pri kraju.
          + (stop.pickupFromName ? `<br><b>roba se uzima u ${stop.pickupFromName}</b>` : '')
          + (stop.requiresTwoPeople ? '<br><b>dva čoveka</b>' : '')
          + '<br><i>Ctrl+klik izbacuje sa ture</i>'
        )
        .addTo(group);

      // Bez Ctrl-a se ne dešava ništa: čioda tada samo pokazuje šta na njoj
      // stoji, što je ono zbog čega je operater i klikne.
      marker.on('click', (event) => {
        if ((event.originalEvent as MouseEvent).ctrlKey) {
          remove.current(stop);
        }
      });
    });

    pickups.forEach((pickup) => {
      L.marker([pickup.latitude, pickup.longitude], {
        icon: pin('pickup', 'P'),
        zIndexOffset: 400
      })
        .bindTooltip(
          `Preuzimanje: ${pickup.label}`
          + `<br>${pickup.pieces} kom — roba se ovde uzima`
        )
        .addTo(group);
    });

    candidates.forEach((candidate) => {
      const marker = L.marker([candidate.stop.latitude, candidate.stop.longitude], {
        icon: pin('candidate', '+')
      })
        .bindTooltip(
          `${candidate.stop.label}${candidate.stop.city ? `, ${candidate.stop.city}` : ''}`
          + `<br><b>≈ +${Math.round(candidate.addedKm)} km</b> turi (procena po vazdušnoj liniji) · ${candidate.stop.pieces} kom`
          // „od pravca", ne „od puta": meri se odstupanje od linije OVOG
          // pravca, a ne udaljenost od najbližeg druma. Stapar je pola
          // kilometra od svog druma, ali trinaest od bečejskog pravca.
          + `<br>${candidate.distanceFromCorridorKm.toFixed(1)} km u stranu od pravca`
          + '<br><i>klikni da dodaš na turu</i>'
        )
        .addTo(group);

      marker.on('click', () => add.current(candidate));
    });

    // Okvir obuhvata i predloge, da se ne izgubi ono što je van pravca.
    const all: [number, number][] = [
      [start.latitude, start.longitude],
      [end.latitude, end.longitude],
      ...corridor.map((p) => [p.latitude, p.longitude] as [number, number]),
      ...stops.map((s) => [s.latitude, s.longitude] as [number, number]),
      ...candidates.map((c) => [c.stop.latitude, c.stop.longitude] as [number, number]),
      ...roadGeometry
    ];

    // Prvo se javlja nova veličina, pa se tek onda računa okvir — obrnutim
    // redom bi okvir bio postavljen po starim merama kontejnera.
    instance.invalidateSize();

    if (all.length > 1) {
      instance.fitBounds(L.latLngBounds(all).pad(0.08));
    }
  }, [start, end, corridor, stops, candidates, pickups, roadGeometry, sizeToken]);

  return <div className="routemap" ref={container} />;
}
